import {Component, OnInit, Input, Optional, OnDestroy, ChangeDetectorRef} from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Router, ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {combineLatest, Subject, switchMap} from 'rxjs';
import { first, map } from 'rxjs/operators';

import { AccountBalanceComponent } from '../account-balance/account-balance.component';
import { AccountSwitcherNonRootComponent } from '../account-switcher-non-root/account-switcher-non-root.component';
import { AccountSwitcherRootComponent } from '../account-switcher-root/account-switcher-root.component';
import { SessionTimeoutService } from '../session-timeout-service/session-timeout.service';
import { DashboardPermissionsService, DashboardService, DashboardStateService } from '../../dashboard/dashboard.module';
import { DashboardApiService } from '../../core/services/api/dashboard-api.service';
import { HeaderService } from '../header-service/header.service';
import { HeaderStateService } from '../header-state-service/header.state.service';
import { RoutingService } from '../../core/services/routing.service';
import { NotificationUserListComponent } from '../notification-user-list/notification-user-list.component';
import { RegisterIconService } from '../../core/services/register-icon.service';
import { environment } from '../../../environments/environment';
import { NotificationApiService } from '../../core/services/api/notification-api.service';
import { ZendeskService } from '../../core/services/zendesk.service';
import { EntityGroupName, Notification } from '../../core/dtos/api';

@Component({
    selector: 'app-dashboard-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss'],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @Input() public headerTitle: string;

  constructor(
    public dashboardStateService: DashboardStateService,
    public headerStateService: HeaderStateService,
    private dashboardService: DashboardService,
    public dashboardApi: DashboardApiService,
    private dashboardRoutingService: RoutingService,
    private dialog: MatDialog,
    private headerService: HeaderService,
    private sessionTimeoutService: SessionTimeoutService,
    private dashboardPermissionsService: DashboardPermissionsService,
    private notificationApi: NotificationApiService,
    private changeDetectorRef: ChangeDetectorRef,
    // RegisterIconService is injected, but not used, to register custom icons.
    private registerIconService: RegisterIconService,
    private zendeskService: ZendeskService,
    @Optional() private router: Router,
    @Optional() private activatedRoute: ActivatedRoute
  ) { }
  ngOnInit$ = new Subject<void>();
  entityName: string;
  userIsAdmin: boolean;
  isClassic = environment.isClassic;
  dashboardUrl = environment.dashboardUrl;
  classicUrl = environment.classicUrl;
  location = window.location;
  environment = environment;

  classicRoute: string;
  classicRoute$Sub = this.ngOnInit$.pipe(
    switchMap(() => this.dashboardRoutingService.classicRoute$)
  ).subscribe(x => {
    this.classicRoute = x;
    this.changeDetectorRef.detectChanges();
  });

  entityGroupName: EntityGroupName;
  entityGroupName$ = this.dashboardPermissionsService.accountWithPermissions$.pipe(map(([x, ]) => x.entity_group_name));
  entityGroupName$Sub = this.ngOnInit$.pipe(
    switchMap(() => this.entityGroupName$)
  ).subscribe(x => {
    this.entityGroupName = x;
    this.changeDetectorRef.detectChanges();
  });

  permissions: {[key: string]: boolean};
  permissions$ = this.dashboardPermissionsService.accountWithPermissions$.pipe(map(([, y]) => this.dashboardPermissionsService.createPermissionLookUpArray(y)));
  permissions$Sub = this.ngOnInit$.pipe(
    switchMap(() => this.permissions$)
  ).subscribe(x => {
    this.permissions = x;
    this.changeDetectorRef.detectChanges();
  });

  userAnnouncementMessages: Notification[] = [];
  userAnnouncementMessages$Sub = this.notificationApi.userAnnouncementMessages$.subscribe(x => this.userAnnouncementMessages = x);
  setup$Sub = this.ngOnInit$.pipe(
    switchMap(() => combineLatest([this.dashboardApi.currentAccount$, this.dashboardApi.currentUserInfo$.pipe(first())]))
  ).subscribe({
    next: ([currentAccount, currentUser]) => {
      this.userIsAdmin = !!currentUser.user_permissions.find((userPermission) => userPermission.entity_group_name === 'root'
        && userPermission.entity_id === this.dashboardStateService.rootPermissions
        && userPermission.deleted_at === null);
      this.dashboardStateService.userIsAdmin = this.userIsAdmin;

      this.dashboardService.setupDashboardStateService(currentAccount, currentUser);
      this.sessionTimeoutService.startSessionTimeOut();
      this.entityName = currentAccount.entity_name === 'root' ? `${currentUser.first_name} ${currentUser.last_name}` : currentAccount.entity_name;

      if (this.userIsAdmin) {
        this.headerService.populateAccountSwitcherRootData();
      } else {
        this.headerService.populateAccountSwitcherNonRootAccounts();
      }
      this.changeDetectorRef.detectChanges();
    },
    error: (errorResponse: HttpErrorResponse) => {
      if (errorResponse.status > 0) {
        this.dashboardRoutingService.directToRoute('Login');
      }
    }
  });

  public ngOnInit() {
    this.ngOnInit$.next();
    this.ngOnInit$.complete();
  }

  public ngOnDestroy() {
    this.classicRoute$Sub.unsubscribe();
    this.entityGroupName$Sub.unsubscribe();
    this.permissions$Sub.unsubscribe();
    this.userAnnouncementMessages$Sub.unsubscribe();
    this.setup$Sub.unsubscribe();
  }

  public openAccountBalance() {
    this.dialog.open(AccountBalanceComponent, {
      autoFocus: false,
      role: 'dialog',
      panelClass: ['account-balance-modal', 'dark'],
      position: {
        right: '12px',
        top: '56px'
      },
      width: '260px',
      height: '136px'
    });
  }

  public openUserNotifications() {
    this.dialog.open(NotificationUserListComponent, {
      role: 'dialog',
      panelClass: ['user-notification-modal', 'dark'],
      position: {
        right: '12px',
        top: '56px'
      }
    });
  }

  public openAccountSwitcher() {
    let dialogConf: MatDialogConfig = {
      role: 'dialog',
      panelClass: ['account-switcher-modal', 'dark'],
      position: {
        right: '80px',
        top: '56px'
      },
      width: '248px',
      autoFocus: false,
    };

    if (this.userIsAdmin) {
      this.dialog.open(AccountSwitcherRootComponent, dialogConf);
    } else {
      this.dialog.open(AccountSwitcherNonRootComponent, dialogConf);
    }
  }

  directToDashboard() {
    this.dashboardRoutingService.directToDashboard();
  }

  public openSupportTab() {
    this.dashboardRoutingService.routeToUrl(`${environment.supportUrl}/hc/en-us`, true);
  }

  public openZendeskHelp() {
    this.zendeskService.toggle();
  }
}
